<script setup>
// §8.4 TimeEntryRow — Client → Bucket · duration · narrative · source.
// Entries have NO status chip (§3.5, §8.4). They carry inline blocker markers
// only — the "needs a bucket" style reason lives at --text-3 with an icon,
// never as a status. Client → Bucket is the canonical hierarchy.
import LIcon from '../base/Icon.vue'
import LEntrySourceBadge from './EntrySourceBadge.vue'

defineProps({
  client: { type: String, default: null },
  bucket: { type: String, default: null }, // firm term: bucket | matter | project (§9)
  duration: { type: String, required: true },
  narrative: { type: String, default: '' },
  source: { type: String, default: 'automated' },
  marker: { type: String, default: null }, // inline blocker reason on this entry
})
</script>

<template>
  <div class="entry" :class="{ blocked: marker }">
    <div class="path">
      <template v-if="client">
        <span class="client">{{ client }}</span>
        <span class="arrow" aria-hidden="true">→</span>
        <span class="bucket">{{ bucket }}</span>
      </template>
      <span v-else class="unbucketed">
        <LIcon name="triangle-exclamation" :size="12" aria-hidden="true" /> Needs a bucket
      </span>
      <div v-if="narrative" class="narrative">{{ narrative }}</div>
      <div v-if="marker" class="marker">
        <LIcon name="triangle-exclamation" :size="11" aria-hidden="true" />{{ marker }}
      </div>
    </div>
    <LEntrySourceBadge :source="source" />
    <div class="dur num">{{ duration }}</div>
  </div>
</template>

<style scoped>
.entry {
  display: grid;
  grid-template-columns: 1fr auto 70px;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-3);
  border-bottom: 1px solid var(--line-soft);
}
.entry:last-child {
  border-bottom: 0;
}
.entry:hover {
  background: var(--surface-sunken);
}
.path {
  min-width: 0;
}
.client {
  font-weight: 600;
  color: var(--text);
}
.arrow {
  color: var(--decorative);
  margin: 0 6px;
}
.bucket {
  color: var(--text-2);
  font-weight: 500;
}
.unbucketed {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--text-3);
  font-weight: 500;
}
.unbucketed :deep(i) {
  color: var(--text-3);
}
.narrative {
  font-size: var(--text-sm);
  color: var(--text-3);
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.marker {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 3px;
  font-size: var(--text-chip);
  color: var(--text-3);
}
.marker :deep(i) {
  color: var(--text-3);
}
.dur {
  text-align: right;
  font-weight: 600;
  color: var(--text-2);
}
</style>
